import {
  fetchPlanIntegrations,
  fetchResponsesForForm,
  loadStepsByPlanId,
  fetchBuiltInInputs,
} from 'actions/flowDesigner';
import {
  fetchPlanConstants,
  fetchAgents,
  fetchLayout,
} from 'actions/flowBlockInput';
import { xAgentFetchDownloadInfoPost } from 'actions/xAgent';
import {
  isConditionStepEnabled,
  isIncidentTypesEnabled,
} from 'selectors/featureToggles';

export const ADD_VALIDATION_CANVAS_DATA = 'ADD_VALIDATION_CANVAS_DATA';
export const VALIDATE_CONSTANT_REMOVED = 'VALIDATE_CONSTANT_REMOVED';
export const VALIDATE_FETCH_PLAN_END_POINTS = 'VALIDATE_FETCH_PLAN_END_POINTS';
export const VALIDATE_FETCH_PLAN_END_POINTS_START = `${VALIDATE_FETCH_PLAN_END_POINTS}_START`;
export const VALIDATE_FETCH_PLAN_END_POINTS_SUCCESS = `${VALIDATE_FETCH_PLAN_END_POINTS}_SUCCESS`;
export const VALIDATE_FETCH_PLAN_END_POINTS_FAILURE = `${VALIDATE_FETCH_PLAN_END_POINTS}_FAIL`;
export const VALIDATE_SCRIPT_STEP_REMOVED = 'VALIDATE_SCRIPT_STEP_REMOVED';
export const RESET_ENDPOINT_TYPE_CHANGED = 'RESET_ENDPOINT_TYPE_CHANGED';
export const VALIDATE_FETCH_FORM_PROPERTIES = 'VALIDATE_FETCH_FORM_PROPERTIES';
export const VALIDATE_RECEIVED_FORM_PROPERTIES =
  'VALIDATE_RECEIVED_FORM_PROPERTIES';

export const VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION =
  'VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION';
export const VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION_SUCCESS = `${VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION}_SUCCESS`;

export const FETCH_CANVAS_FOR_VALIDATION = 'FETCH_CANVAS_FOR_VALIDATION';
export const FETCH_CANVAS_FOR_VALIDATION_START = `${FETCH_CANVAS_FOR_VALIDATION}_START`;
export const FETCH_CANVAS_FOR_VALIDATION_SUCCESS = `${FETCH_CANVAS_FOR_VALIDATION}_SUCCESS`;
export const FETCH_CANVAS_FOR_VALIDATION__FAIL = `${FETCH_CANVAS_FOR_VALIDATION}_FAIL`;

export const VALIDATE_DELETED_CANVAS = 'VALIDATE_DELETED_CANVAS';

export function fetchPlanEndpoints(planId) {
  const uri = `/webapi/xm/1/plans/${planId}/endpoints`;
  return {
    type: VALIDATE_FETCH_PLAN_END_POINTS,
    meta: {
      xmApi: true,
      uri,
      method: 'get',
    },
  };
}

export function fetchFormProperties(planId, formId, type) {
  const url = `/webapi/xm/1/plans/${planId}/forms/${formId}/property-definitions`;
  return {
    type,
    meta: {
      xmApi: true,
      uri: url,
      method: 'get',
    },
  };
}

export const FETCH_INCIDENT_TYPES_AND_PROPERTIES =
  'FETCH_INCIDENT_TYPES_AND_PROPERTIES';
export const FETCH_INCIDENT_TYPES_AND_PROPERTIES_START = `${FETCH_INCIDENT_TYPES_AND_PROPERTIES}_START`;
export const FETCH_INCIDENT_TYPES_AND_PROPERTIES_SUCCESS = `${FETCH_INCIDENT_TYPES_AND_PROPERTIES}_SUCCESS`;
export const FETCH_INCIDENT_TYPES_AND_PROPERTIES__FAIL = `${FETCH_INCIDENT_TYPES_AND_PROPERTIES}_FAIL`;

export function fetchIncientTypesAndProperties() {
  const url = `/webapi/xm/1/incident-types?embed=properties`;
  return {
    type: FETCH_INCIDENT_TYPES_AND_PROPERTIES,
    meta: {
      xmApi: true,
      uri: url,
      method: 'get',
    },
  };
}

// TODO: flowValidation reducer doesn't do anything with this yet.
export function constantRemoved(stepId) {
  return {
    type: VALIDATE_CONSTANT_REMOVED,
    payload: stepId,
  };
}

export function scriptStepRemoved(stepId) {
  return {
    type: VALIDATE_SCRIPT_STEP_REMOVED,
    payload: stepId,
  };
}

function fetchAndValidateCanvas({ canvasId }) {
  return (dispatch, getState) => {
    let embed = 'blocks,edges,inputs,switchCases,mergeOutputs,dynamicOutputs';
    if (isConditionStepEnabled(getState())) {
      embed += ',conditionPaths';
    }

    return dispatch({
      type: FETCH_CANVAS_FOR_VALIDATION,
      meta: {
        xmApi: true,
        uri: `/webapi/xm/1/flow-canvases/${canvasId}`,
        method: 'getAllWithEmbeds',
        params: {
          embed,
        },
      },
    });
  };
}

function addCanvasData(payload) {
  return {
    type: ADD_VALIDATION_CANVAS_DATA,
    payload,
  };
}

function receivedFormProperties({ canvasId, properties, layout }) {
  return {
    type: VALIDATE_RECEIVED_FORM_PROPERTIES,
    payload: { canvasId, properties, layout },
  };
}

export const FLOW_VALIDATION_SET_PLAN_ID = 'FLOW_VALIDATION_SET_PLAN_ID';
export function validateCanvases({ planId, allCanvases, forms }) {
  return (dispatch, getState) => {
    dispatch({
      type: FLOW_VALIDATION_SET_PLAN_ID,
      payload: planId,
    });

    const incidentTypeRequest = isIncidentTypesEnabled(getState())
      ? dispatch(fetchIncientTypesAndProperties())
      : Promise.resolve();

    return Promise.all([
      dispatch(loadStepsByPlanId({ planId })),
      dispatch(fetchBuiltInInputs()),
      dispatch(fetchPlanConstants(planId)),
      dispatch(fetchPlanEndpoints(planId)),
      dispatch(fetchPlanIntegrations(planId)),
      dispatch(fetchAgents()),
      dispatch(xAgentFetchDownloadInfoPost()),
      incidentTypeRequest,
      ...forms.map(form =>
        dispatch(
          fetchLayout(form.id, VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION),
        ),
      ),
      ...forms.map(form =>
        dispatch(
          fetchResponsesForForm(planId, form.id, 'FETCH_ALL_FORM_RESPONSES'),
        ),
      ),
      ...forms.map(form =>
        dispatch(
          fetchFormProperties(planId, form.id, VALIDATE_FETCH_FORM_PROPERTIES),
        ).then(formProperties => {
          const canvas = allCanvases.find(c => c?.form?.id === form.id);
          return canvas
            ? dispatch(
                receivedFormProperties({
                  canvasId: canvas.id,
                  properties: formProperties.payload.data,
                }),
              )
            : Promise.resolve();
        }),
      ),
      ...allCanvases.map(canvas => {
        if (canvas.form && canvas.initial) {
          return dispatch(addCanvasData(canvas));
        }
        return dispatch(fetchAndValidateCanvas({ canvasId: canvas.id }));
      }),
    ]);
  };
}

// Fetching and validating canvases to use data for forms
export function shallowValidationOfCanvases({ planId, allCanvases }) {
  return dispatch => {
    dispatch({
      type: FLOW_VALIDATION_SET_PLAN_ID,
      payload: planId,
    });

    return Promise.all([
      dispatch(loadStepsByPlanId({ planId })),
      dispatch(fetchPlanIntegrations(planId)),
    ]).then(() => {
      const listOfPromises = allCanvases.map(c => {
        if (!c.initial) {
          return dispatch(fetchAndValidateCanvas({ canvasId: c.id }));
        }
        return dispatch(addCanvasData(c));
      });
      return Promise.all(listOfPromises);
    });
  };
}

export function validateDeletedCanvas({ id }) {
  return {
    type: VALIDATE_DELETED_CANVAS,
    payload: id,
  };
}
